import React from 'react';
import {
  getSwingDoorConfig,
  isSwingDoor,
  isFrenchDoor,
  isSlidingDoor,
  isPocketDoor,
  isGarageDoor,
  isLeftHanded,
  garageSectionCount,
  DOOR_HANDLE_HEIGHT_M,
  DOOR_HINGE_BARREL_HEIGHT_M,
  doorHingeCentersFromTop,
} from '../../../constants/doorTypes';
import { computeSlidingPanelLayout } from '../../3d/geometry/slidingPanelLayout';

/**
 * Procedural door schematic for elevation views. Dispatches on
 * `category.door.doorType` (helpers from constants/doorTypes.js)
 * and `category.door.style` (panel style: singlePanel / twoPanel /
 * sixPanel / singleGlass). Falls back to a plain leaf without hardware
 * when the doorType isn't recognised so the renderer never goes blank.
 *
 * DRAWN IN METRES. The object's box is the door OPENING — the same
 * `width × height` `DoorWithTrim` builds from — so every part is placed
 * where the 3D model puts it, in real inches, whatever the door's size:
 *
 *   - the LEAF fills the opening and runs to the floor, less a ¾"
 *     undercut. (The first version drew it in a 100×100 box inset 6% on
 *     every side, which on a 7' door lifted the leaf 5" off the floor and
 *     drew a sill line under a swing door.)
 *   - the JAMB is the opening edge; the CASING sits OUTSIDE it, head and
 *     legs only — there is no bottom piece on a doorway, the legs die into
 *     the floor.
 *   - HINGES at 7" from the top, centre, 11" from the bottom — the 3D
 *     model's positions, from one shared rule.
 *   - the HANDLE at 36" from the floor (`DOOR_HANDLE_HEIGHT_M`), on the
 *     latch edge.
 *   - panel layouts (two / six / glass) use the 3D builders' stile and
 *     rail widths.
 *
 * Everything is placed relative to the opening box, and the SVG's
 * overflow is visible, so the casing draws on the wall around the box.
 *
 * Front view: leaf, casing, hardware. Side view: thin slab profile plus
 * a handle stub. Mirrored for L vs R.
 *
 * Stroke / selection / capture wiring is owned by the caller and
 * passed in via props — this component renders only SVG primitives.
 */

const IN = 0.0254;

// `DoorWithTrim` defaults: trimWidth 0.1, 3 mm leaf clearance per side,
// 4¾" stile/rail border on panelled leaves, ½" sliding-panel overlap.
const CASING_W = 0.1;
const LEAF_CLEARANCE = 0.003;
const PANEL_BORDER = 0.12065;
const SLIDING_OVERLAP = 0.0127;
// A hung leaf clears the finished floor by ~¾".
const UNDERCUT = 0.75 * IN;
// Latch-side hardware: 2¾" backset, a 2¼" rose, a 4½" lever.
const HANDLE_BACKSET = 2.75 * IN;
const ROSE_R = 1.125 * IN;
const LEVER_LEN = 4.5 * IN;
const KNOB_R = 1.1 * IN;
const HINGE_BARREL_W = 1 * IN;
const EDGE_PULL_H = 6 * IN;
const EDGE_PULL_INSET = 1.5 * IN;
const SLIDING_TRACK_H = 1 * IN;
const GLASS_FILL = 'rgba(147, 197, 253, 0.22)';

function DoorElevationSchematic({
  obj,
  strokeColor,
  strokeWidth,
  view = 'front',
  widthMeters,
  heightMeters,
  depthMeters,
}) {
  const w = widthMeters ?? obj?.dimensions?.x ?? obj?.dimensions?.width ?? 0.9;
  const h =
    heightMeters ?? obj?.dimensions?.y ?? obj?.dimensions?.height ?? 2.1;
  if (view === 'left' || view === 'right') {
    return (
      <DoorSideView
        view={view}
        depthMeters={depthMeters}
        heightMeters={h}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
    );
  }
  return (
    <DoorFrontView
      obj={obj}
      w={w}
      h={h}
      strokeColor={strokeColor}
      strokeWidth={strokeWidth}
    />
  );
}

DoorElevationSchematic.supportsSideViews = true;

function DoorFrontView({ obj, w, h, strokeColor, strokeWidth }) {
  const doorCat =
    obj && typeof obj.category?.door === 'object' ? obj.category.door : {};
  const doorType = doorCat.doorType;
  const panelStyle = doorCat.style || 'singlePanel';
  const handleStyle = doorCat.handleStyle || 'lever';
  const wfpr = obj?.wallFacesPriorityRoom;
  // The door's local +X against the drawing's right. Absent (gallery,
  // tests) the door is read as drawn from its own front.
  const dirAligned = obj?.doorDirAligned !== false;
  const pen = { strokeColor, strokeWidth };
  const box = { w, h };

  let inner;
  let casing = true;
  if (doorType && isFrenchDoor(doorType)) {
    inner = (
      <FrenchDoorInner
        box={box}
        panelStyle={panelStyle}
        handleStyle={handleStyle}
        pen={pen}
      />
    );
  } else if (doorType && isGarageDoor(doorType)) {
    casing = false;
    inner = <GarageDoorInner box={box} pen={pen} />;
  } else if (doorType && isSlidingDoor(doorType)) {
    inner = (
      <SlidingDoorInner
        box={box}
        panelCount={Math.max(2, doorCat.slidingPanelCount || 2)}
        handleOnLeft={isLeftHanded(doorType) === dirAligned}
        panelStyle={panelStyle}
        pen={pen}
      />
    );
  } else if (doorType && isPocketDoor(doorType)) {
    inner = (
      <PocketDoorInner
        box={box}
        panelCount={Math.max(1, doorCat.slidingPanelCount || 1)}
        handleOnLeft={isLeftHanded(doorType) === dirAligned}
        panelStyle={panelStyle}
        pen={pen}
      />
    );
  } else if (doorType && isSwingDoor(doorType)) {
    const { hingeAtStart } = getSwingDoorConfig(doorType, wfpr);
    const hingeOnLeft = hingeAtStart === dirAligned;
    inner = (
      <SwingDoorInner
        box={box}
        panelStyle={panelStyle}
        handleStyle={handleStyle}
        hingeOnLeft={hingeOnLeft}
        pen={pen}
      />
    );
  } else {
    inner = (
      <SwingDoorInner
        box={box}
        panelStyle={panelStyle}
        handleStyle={handleStyle}
        hingeOnLeft={false}
        pen={pen}
        suppressHardware
      />
    );
  }

  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="none"
      width="100%"
      height="100%"
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        overflow: 'visible',
      }}
    >
      {casing && <Casing box={box} pen={pen} />}
      {/* The opening: jamb legs and head. No sill — it is a doorway. */}
      <path
        d={`M 0 ${h} L 0 0 L ${w} 0 L ${w} ${h}`}
        fill="none"
        {...strokeProps(pen)}
      />
      {inner}
    </svg>
  );
}

function strokeProps({ strokeColor, strokeWidth }) {
  return {
    stroke: strokeColor,
    strokeWidth,
    vectorEffect: 'non-scaling-stroke',
  };
}

/**
 * Casing around the opening: a band `CASING_W` wide outside the jambs
 * and above the head, mitred at the two top corners, legs running to
 * the floor. Drawn as its outer outline plus the mitre lines — the inner
 * edge is the opening itself.
 */
function Casing({ box, pen }) {
  const { w, h } = box;
  const c = CASING_W;
  return (
    <>
      <path
        d={`M ${-c} ${h} L ${-c} ${-c} L ${w + c} ${-c} L ${w + c} ${h}`}
        fill="none"
        {...strokeProps(pen)}
      />
      <line x1={-c} y1={-c} x2={0} y2={0} {...strokeProps(pen)} />
      <line x1={w + c} y1={-c} x2={w} y2={0} {...strokeProps(pen)} />
    </>
  );
}

/** A hung leaf's rectangle inside the opening: 3 mm off each jamb, ¾" off the floor. */
function hungLeafRect(box) {
  return {
    x: LEAF_CLEARANCE,
    y: 0,
    w: box.w - LEAF_CLEARANCE * 2,
    h: box.h - UNDERCUT,
  };
}

function SwingDoorInner({
  box,
  panelStyle,
  handleStyle,
  hingeOnLeft,
  pen,
  suppressHardware = false,
}) {
  const leaf = hungLeafRect(box);
  return (
    <>
      <Leaf rect={leaf} style={panelStyle} pen={pen} />
      {!suppressHardware && (
        <>
          <Hinges leaf={leaf} onLeft={hingeOnLeft} pen={pen} />
          <Handle
            leaf={leaf}
            latchOnLeft={!hingeOnLeft}
            style={handleStyle}
            pen={pen}
          />
        </>
      )}
    </>
  );
}

function FrenchDoorInner({ box, panelStyle, handleStyle, pen }) {
  // Mirrored pair hung on the outer jambs, meeting at the centre astragal;
  // both handles on the meeting stiles.
  const { w } = box;
  const full = hungLeafRect(box);
  const leafW = w / 2 - LEAF_CLEARANCE * 2;
  const leafL = { ...full, x: LEAF_CLEARANCE, w: leafW };
  const leafR = { ...full, x: w / 2 + LEAF_CLEARANCE, w: leafW };
  return (
    <>
      <Leaf rect={leafL} style={panelStyle} pen={pen} />
      <Leaf rect={leafR} style={panelStyle} pen={pen} />
      <Hinges leaf={leafL} onLeft pen={pen} />
      <Hinges leaf={leafR} onLeft={false} pen={pen} />
      <Handle leaf={leafL} latchOnLeft={false} style={handleStyle} pen={pen} />
      <Handle leaf={leafR} latchOnLeft style={handleStyle} pen={pen} />
    </>
  );
}

/**
 * The sliding panels as `DoorWithTrim` lays them out: N panels of
 * `(w + (N-1)·overlap) / N`, each stepping by a panel less the overlap,
 * on a track along the floor. The edge pull sits on the leading edge
 * of each slider that carries one, and each slider's arrow points the
 * way it travels to open.
 */
function SlidingDoorInner({ box, panelCount, handleOnLeft, panelStyle, pen }) {
  const { w, h } = box;
  const layout = computeSlidingPanelLayout(panelCount, handleOnLeft);
  const { N, handleEdges, slideFactors } = layout;
  const panelW = (w + (N - 1) * SLIDING_OVERLAP) / N;
  const stride = panelW - SLIDING_OVERLAP;
  const panelH = h - SLIDING_TRACK_H;
  // A flush leaf reads as a wall on a patio slider; the 3D forces glass too.
  const style = panelStyle === 'singlePanel' ? 'singleGlass' : panelStyle;
  const handleY = h - DOOR_HANDLE_HEIGHT_M;
  const panels = [];
  for (let i = 0; i < N; i += 1) {
    const rect = { x: i * stride, y: 0, w: panelW, h: panelH };
    panels.push(
      <React.Fragment key={`panel-${i}`}>
        <Leaf rect={rect} style={style} pen={pen} />
        {handleEdges[i] && (
          <EdgePull
            x={
              handleEdges[i] === 'left'
                ? rect.x + EDGE_PULL_INSET
                : rect.x + rect.w - EDGE_PULL_INSET
            }
            y={handleY}
            pen={pen}
          />
        )}
        {slideFactors[i] !== 0 && (
          <SlideArrow
            cx={rect.x + rect.w / 2}
            cy={handleY}
            length={Math.min(0.3, rect.w * 0.4)}
            facingRight={slideFactors[i] < 0 ? false : true}
            pen={pen}
          />
        )}
      </React.Fragment>
    );
  }
  return (
    <>
      {panels}
      <Track box={box} pen={pen} />
    </>
  );
}

/**
 * Pocket panels stack like sliders, but ALL of them travel — into the
 * wall on the side away from the pull. The pocketed position is drawn
 * as hidden (dashed) lines beside the opening: one panel's width, since
 * the stack nests there.
 */
function PocketDoorInner({ box, panelCount, handleOnLeft, panelStyle, pen }) {
  const { w, h } = box;
  const N = Math.max(1, Math.min(3, panelCount));
  const panelW =
    N === 1 ? w - LEAF_CLEARANCE * 2 : (w + (N - 1) * SLIDING_OVERLAP) / N;
  const stride = panelW - SLIDING_OVERLAP;
  const leaf = hungLeafRect(box);
  const handleY = h - DOOR_HANDLE_HEIGHT_M;
  // Travel: away from the pull.
  const dir = handleOnLeft ? 1 : -1;
  const panels = [];
  for (let i = 0; i < N; i += 1) {
    const rect = N === 1 ? leaf : { x: i * stride, y: 0, w: panelW, h: leaf.h };
    panels.push(
      <React.Fragment key={`panel-${i}`}>
        <Leaf rect={rect} style={panelStyle} pen={pen} />
        <SlideArrow
          cx={rect.x + rect.w / 2}
          cy={handleY}
          length={Math.min(0.3, rect.w * 0.4)}
          facingRight={dir > 0}
          pen={pen}
        />
      </React.Fragment>
    );
  }
  const leadIdx = handleOnLeft ? 0 : N - 1;
  const lead = N === 1 ? leaf : { x: leadIdx * stride, w: panelW };
  const pullX = handleOnLeft
    ? lead.x + EDGE_PULL_INSET
    : lead.x + lead.w - EDGE_PULL_INSET;
  const pocketX = handleOnLeft ? w : -panelW;
  return (
    <>
      {panels}
      <EdgePull x={pullX} y={handleY} pen={pen} />
      <rect
        x={pocketX}
        y={0}
        width={panelW}
        height={leaf.h}
        fill="none"
        strokeDasharray="0.04 0.025"
        {...strokeProps(pen)}
      />
    </>
  );
}

function GarageDoorInner({ box, pen }) {
  // An overhead sectional door: full-width courses standing on the slab,
  // no handle and no hinges — it is opener-operated. Course count comes
  // from the shared rule so this matches the 3D model exactly.
  const { w, h } = box;
  const sections = garageSectionCount(h);
  const joints = [];
  for (let i = 1; i < sections; i++) {
    const y = (h * i) / sections;
    joints.push(
      <line key={i} x1={0} y1={y} x2={w} y2={y} {...strokeProps(pen)} />
    );
  }
  return joints;
}

/** Three hinge barrels on one edge of a leaf, at the 3D model's heights. */
function Hinges({ leaf, onLeft, pen }) {
  const x = onLeft ? leaf.x : leaf.x + leaf.w;
  return doorHingeCentersFromTop(leaf.h).map((cy) => (
    <rect
      key={cy}
      x={x - HINGE_BARREL_W / 2}
      y={leaf.y + cy - DOOR_HINGE_BARREL_HEIGHT_M / 2}
      width={HINGE_BARREL_W}
      height={DOOR_HINGE_BARREL_HEIGHT_M}
      fill={pen.strokeColor}
      stroke="none"
    />
  ));
}

/** Lever on a rose, or a knob, at handle height on the latch edge. */
function Handle({ leaf, latchOnLeft, style, pen }) {
  const cx = latchOnLeft
    ? leaf.x + HANDLE_BACKSET
    : leaf.x + leaf.w - HANDLE_BACKSET;
  const cy = leaf.y + leaf.h + UNDERCUT - DOOR_HANDLE_HEIGHT_M;
  if (style === 'knob') {
    return (
      <circle cx={cx} cy={cy} r={KNOB_R} fill="none" {...strokeProps(pen)} />
    );
  }
  // Lever points away from the latch edge, toward the hinges.
  const tipX = latchOnLeft ? cx + LEVER_LEN : cx - LEVER_LEN;
  return (
    <>
      <circle cx={cx} cy={cy} r={ROSE_R} fill="none" {...strokeProps(pen)} />
      <line
        x1={cx}
        y1={cy}
        x2={tipX}
        y2={cy}
        strokeLinecap="round"
        {...strokeProps(pen)}
        strokeWidth={pen.strokeWidth * 2.5}
      />
    </>
  );
}

/** A flush vertical pull on a sliding / pocket panel's leading edge. */
function EdgePull({ x, y, pen }) {
  return (
    <line
      x1={x}
      y1={y - EDGE_PULL_H / 2}
      x2={x}
      y2={y + EDGE_PULL_H / 2}
      strokeLinecap="round"
      {...strokeProps(pen)}
      strokeWidth={pen.strokeWidth * 2.5}
    />
  );
}

/** The bottom track a slider runs on. */
function Track({ box, pen }) {
  const { w, h } = box;
  return (
    <rect
      x={0}
      y={h - SLIDING_TRACK_H}
      width={w}
      height={SLIDING_TRACK_H}
      fill="none"
      {...strokeProps(pen)}
    />
  );
}

function SlideArrow({ cx, cy, length, facingRight, pen }) {
  const dir = facingRight ? 1 : -1;
  const half = length / 2;
  const head = Math.min(0.05, length / 4);
  const x1 = cx - half * dir;
  const x2 = cx + half * dir;
  return (
    <path
      d={
        `M ${x1} ${cy} L ${x2} ${cy} ` +
        `M ${x2 - head * dir} ${cy - head} L ${x2} ${cy} L ${x2 - head * dir} ${cy + head}`
      }
      fill="none"
      strokeLinejoin="round"
      {...strokeProps(pen)}
    />
  );
}

/**
 * One leaf: its outline plus the panel layout for its style, using the
 * `DoorWithTrim` builders' proportions —
 *   singlePanel: a flush slab, nothing inside
 *   twoPanel:    4¾" stiles and rails, a 2" lock rail, two equal panels
 *   sixPanel:    4¾" border, 3⅛" centre stile, 1½" rails; rows 22/39/39 %
 *   singleGlass: one lite inside a 4¾" border
 */
function Leaf({ rect, style, pen }) {
  const sub = (x, y, w, h, fill) =>
    w > 0 && h > 0 ? (
      <rect
        key={`${x}-${y}`}
        x={rect.x + x}
        y={rect.y + y}
        width={w}
        height={h}
        fill={fill || 'none'}
        {...strokeProps(pen)}
      />
    ) : null;

  const outline = (
    <rect
      x={rect.x}
      y={rect.y}
      width={rect.w}
      height={rect.h}
      fill="none"
      {...strokeProps(pen)}
    />
  );
  const b = PANEL_BORDER;
  const innerW = rect.w - b * 2;

  if (style === 'singleGlass') {
    return (
      <>
        {outline}
        {sub(b, b, innerW, rect.h - b * 2, GLASS_FILL)}
      </>
    );
  }
  if (style === 'twoPanel') {
    const gap = 0.05;
    const panelH = (rect.h - b * 2 - gap) / 2;
    return (
      <>
        {outline}
        {sub(b, b, innerW, panelH)}
        {sub(b, b + panelH + gap, innerW, panelH)}
      </>
    );
  }
  if (style === 'sixPanel') {
    const centerStile = 0.08;
    const gap = 0.04;
    const panelW = (innerW - centerStile) / 2;
    const available = rect.h - b * 2 - gap * 2;
    const rows = [available * 0.22, available * 0.39, available * 0.39];
    let y = b;
    const cells = [];
    rows.forEach((rowH) => {
      cells.push(sub(b, y, panelW, rowH));
      cells.push(sub(b + panelW + centerStile, y, panelW, rowH));
      y += rowH + gap;
    });
    return (
      <>
        {outline}
        {cells}
      </>
    );
  }
  // singlePanel (default): a flush slab.
  return outline;
}

/**
 * Side elevation of a door: thin slab (full depth × full height) plus
 * a small handle stub protruding from the FRONT edge at handle height.
 * LEFT view shows the door's left side — conventionally the "front"
 * face (where the handle is) is on the right side of the drawing.
 * RIGHT view mirrors.
 *
 * Renders in meter-space against `depthMeters × heightMeters` so the
 * card strip's aspect-ratio container scales it correctly.
 */
function DoorSideView({
  view,
  depthMeters,
  heightMeters,
  strokeColor,
  strokeWidth,
}) {
  const w = depthMeters;
  const h = heightMeters;
  const pen = { strokeColor, strokeWidth };
  const handleY = h - DOOR_HANDLE_HEIGHT_M;
  const handleProtrusion = Math.min(w * 0.35, 0.04);
  const handleHeight = Math.min(h * 0.012, 0.03);
  // Front edge of the door: where the handle is. Convention: for the
  // LEFT view the front face points to the right in the drawing;
  // mirror via scale(-1, 1) for the RIGHT view.
  const sideChildren = (
    <path
      d={
        `M ${w} ${handleY - handleHeight / 2} ` +
        `L ${w + handleProtrusion} ${handleY - handleHeight / 2} ` +
        `L ${w + handleProtrusion} ${handleY + handleHeight / 2} ` +
        `L ${w} ${handleY + handleHeight / 2}`
      }
      fill="none"
      {...strokeProps(pen)}
    />
  );
  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="xMidYMid meet"
      style={{ width: '100%', height: '100%', overflow: 'visible' }}
    >
      {/* Bounding frame (jamb-width slab silhouette) */}
      <rect
        x={0}
        y={0}
        width={w}
        height={h}
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
        {...strokeProps(pen)}
      />
      {view === 'right' ? (
        <g transform={`translate(${w}, 0) scale(-1, 1)`}>{sideChildren}</g>
      ) : (
        sideChildren
      )}
    </svg>
  );
}

export default DoorElevationSchematic;
